import { useBottomPanel } from '@ubnt/link-core-ui/components/BottomPanel/context/BottomPanelContext';
import { useCallback, useMemo } from 'react';
import { countrySupportsAfc } from '@ubnt/wifi-calculator/utils/regulatoryLimits';

import { useDispatch, useSelector } from 'app/store';
import {
  devicesDeviceAfcSelector,
  devicesDeviceInstanceSelector,
  devicesDeviceIsOutdoorModeSelector,
  devicesAfcChange,
  devicesDeviceIsAfcForcedSelector,
} from 'modules/devices/slice';
import { settingsCountrySelector } from 'modules/settings/slice';
import { trackEvent } from 'utils/googleAnalytics';

import MLOCheckbox, { MLOCheckboxProps } from '../components/elements/MLOCheckbox';

const tooltipDescription = (
  <>
    <span>AFC (Automatic Frequency Coordination) is a feature in </span>
    <b>UniFi Network</b>
    <span> designed for 6 GHz WiFi that increases transmit power while complying with</span>
    <b> FCC Standard Power Limits</b>
    <span>
      . It ensures devices operate on available frequencies without interfering with licensed users, such as satellite
      and fixed wireless services. Outdoor rated APs require AFC, while indoor rated APs can use it for extended range
      on 6 GHz.
    </span>{' '}
    <a
      href="https://help.ui.com/hc/en-us/articles/25707346939927-6-GHz-Extended-Range-AFC-in-UniFi"
      target="_blank"
      rel="noreferrer"
    >
      Learn more
    </a>
  </>
);

const AFCCheckboxContainer = (props: Partial<MLOCheckboxProps>) => {
  const { leftId } = useBottomPanel();

  const country = useSelector(settingsCountrySelector);
  const isCountrySupportsAfc = useMemo(() => countrySupportsAfc(country), [country]);

  const deviceSupportsAfc = useSelector((state) =>
    leftId ? devicesDeviceInstanceSelector(state, leftId).supportsAfc : undefined
  );
  const devicesIsOutdoorMode = !!useSelector((state) =>
    leftId ? devicesDeviceIsOutdoorModeSelector(state, leftId) : undefined
  );
  const isAfcForced = useSelector((state) => (leftId ? devicesDeviceIsAfcForcedSelector(state, leftId) : false));

  const checked =
    !!useSelector((state) => (leftId ? devicesDeviceAfcSelector(state, leftId) : undefined)) || devicesIsOutdoorMode;

  const dispatch = useDispatch();

  const onChange = useCallback(
    (afc: boolean) => {
      if (leftId) {
        dispatch(devicesAfcChange({ id: leftId, afc, country }));
        trackEvent('bottomPanelAfcChange', { afc: afc.toString() });
      }
    },
    [dispatch, leftId, country]
  );

  if (!isCountrySupportsAfc || !deviceSupportsAfc) {
    return null;
  }

  return (
    <MLOCheckbox
      disabled={devicesIsOutdoorMode || !!isAfcForced}
      checked={checked}
      onChange={onChange}
      label="AFC"
      infoText={tooltipDescription}
      infoIndicatorSeverity="info"
      {...props}
    />
  );
};

export default AFCCheckboxContainer;
